import * as THREE from "three";
import { IFace } from "./IFace";

export interface PulseHighlighterOptions {
	getSceneGroup: () => THREE.Group | null;
	getZoom: () => number;
	getModelExtent: () => number;
}

/**
 * Draws a short-lived, pulsating red dot at the centroid of a face so the user
 * can spot small highlighted areas in the CAD view. All state (sprites, texture,
 * animation loop) is self-contained; the caller only supplies the faces to pulse.
 */
export class PulseHighlighter {
	private texture: THREE.CanvasTexture | null = null;
	private sprites: THREE.Sprite[] = [];
	private animationRunning = false;
	private readonly durationMs = 2000;
	private readonly minScreenPixels = 24;

	constructor(private readonly options: PulseHighlighterOptions) {}

	/**
	 * Adds a pulsating red dot at the centroid of each face. Multiple calls stack -
	 * every new call adds its own set of dots on top of the ones already running.
	 */
	public pulse(faces: IFace[]) {
		const sceneGroup = this.options.getSceneGroup();
		if (!sceneGroup || faces.length === 0) {
			return;
		}

		if (!this.texture) {
			this.texture = this.createDotTexture();
		}

		// Preferred size relative to the model so the dot is visible on both large and
		// tiny parts. The animation additionally clamps to a minimum on-screen pixel size.
		const preferredWorldSize = Math.max(this.options.getModelExtent() * 0.04, 1);
		const startTime = performance.now();

		for (const face of faces) {
			if (!face.points || face.points.length === 0) {
				continue;
			}
			const center = this.computeCentroid(face);
			const material = new THREE.SpriteMaterial({
				map: this.texture,
				color: 0xff0000,
				transparent: true,
				depthTest: false,
				depthWrite: false,
			});
			const sprite = new THREE.Sprite(material);
			sprite.position.copy(center);
			sprite.scale.set(preferredWorldSize, preferredWorldSize, 1);
			sprite.renderOrder = 999;
			sprite.name = "PulsingHighlight";
			sprite.userData = { preferredWorldSize, startTime };
			// Disable raycasting so the dot never intercepts hover/click intersections
			// against the underlying face or edge in CadView's raycaster.
			sprite.raycast = () => {};
			sceneGroup.add(sprite);
			this.sprites.push(sprite);
		}

		if (!this.animationRunning && this.sprites.length > 0) {
			this.animationRunning = true;
			requestAnimationFrame(() => this.animate());
		}
	}

	private computeCentroid(face: IFace): THREE.Vector3 {
		const center = new THREE.Vector3();
		for (const p of face.points) {
			center.x += p.pos[0];
			center.y += p.pos[1];
			center.z += p.pos[2];
		}
		center.divideScalar(face.points.length);
		return center;
	}

	private animate() {
		const now = performance.now();
		// Camera.zoom is ~"pixels per world unit" for the orthographic view set up in
		// CadView. Turn the desired screen-pixel minimum into a world-space minimum
		// so the dot stays findable even when the view is zoomed far out.
		const zoom = this.options.getZoom() > 0 ? this.options.getZoom() : 1;
		const remaining: THREE.Sprite[] = [];
		for (const sprite of this.sprites) {
			const { preferredWorldSize, startTime } = sprite.userData as {
				preferredWorldSize: number;
				startTime: number;
			};
			const elapsed = now - startTime;
			if (elapsed >= this.durationMs) {
				this.disposeSprite(sprite);
				continue;
			}
			const minWorldSize = this.minScreenPixels / zoom;
			const effectiveBase = Math.max(preferredWorldSize, minWorldSize);
			// Pulse: ~2 Hz oscillation between 70% and 130% of the base size.
			const pulse = 1 + 0.3 * Math.sin(elapsed / 130);
			// Linear fade so the dot disappears cleanly.
			const fade = 1 - elapsed / this.durationMs;
			const scale = effectiveBase * pulse;
			sprite.scale.set(scale, scale, 1);
			(sprite.material as THREE.SpriteMaterial).opacity = fade;
			remaining.push(sprite);
		}
		this.sprites = remaining;
		if (this.sprites.length > 0) {
			requestAnimationFrame(() => this.animate());
		} else {
			this.animationRunning = false;
		}
	}

	/**
	 * Fully detaches a sprite from the scene, disposes its material and drops any
	 * references so it can be garbage collected. The shared dot texture is kept for
	 * later pulses and disposed only by an explicit {@link dispose} call.
	 */
	private disposeSprite(sprite: THREE.Sprite) {
		sprite.removeFromParent();
		const material = sprite.material as THREE.SpriteMaterial;
		material.map = null;
		material.dispose();
		sprite.userData = {};
	}

	/**
	 * Cancels all running pulses immediately and releases GPU resources. Call this
	 * when the CAD scene is being torn down or the model is fully replaced.
	 */
	public dispose() {
		for (const sprite of this.sprites) {
			this.disposeSprite(sprite);
		}
		this.sprites = [];
		this.animationRunning = false;
		if (this.texture) {
			this.texture.dispose();
			this.texture = null;
		}
	}

	private createDotTexture(): THREE.CanvasTexture {
		const size = 128;
		const canvas = document.createElement("canvas");
		canvas.width = size;
		canvas.height = size;
		const ctx = canvas.getContext("2d")!;
		const center = size / 2;
		const gradient = ctx.createRadialGradient(center, center, 0, center, center, center);
		gradient.addColorStop(0, "rgba(255, 0, 0, 1)");
		gradient.addColorStop(0.35, "rgba(255, 0, 0, 0.85)");
		gradient.addColorStop(0.7, "rgba(255, 0, 0, 0.25)");
		gradient.addColorStop(1, "rgba(255, 0, 0, 0)");
		ctx.fillStyle = gradient;
		ctx.beginPath();
		ctx.arc(center, center, center, 0, Math.PI * 2);
		ctx.fill();
		const texture = new THREE.CanvasTexture(canvas);
		texture.needsUpdate = true;
		return texture;
	}
}
